import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Pressable, ScrollView, Text, View } from 'react-native';
import { logger } from '~/lib/logger';
import { palettes, tokens, type } from '~/theme';

/**
 * Catches a render-time throw and shows a screen instead of killing the app.
 *
 * A release build has no red box. Any exception thrown during render, in a lifecycle
 * method, or in a constructor tears the whole process down — the app vanishes with no
 * message. That is exactly what a single `null` from GET /v1/jobs did: `jobs.length` on
 * the LANDING screen took the app out three launches in a row, and from the operator's
 * side it was indistinguishable from the app being broken on install. One bad field in one
 * response should cost a screen, not the session.
 *
 * Deliberately NOT themed through useTheme. This is a class component because
 * componentDidCatch has no hook equivalent, and more importantly the outer instance of
 * this boundary sits ABOVE ThemeProvider so it can catch a provider crash — at which
 * point there is no context to read. Colour and type come from the token modules
 * directly, which keeps the no-hex-codes rule intact without needing React context to
 * survive.
 *
 * It does not catch async rejections or native callbacks (an error boundary never does),
 * so the try/catch in the data hooks is still load-bearing.
 */
interface Props {
  children: ReactNode;
  /** Named in the fallback copy so the operator can say where it happened. */
  label?: string;
}

interface State {
  error: Error | null;
  /** Bumped on retry so children genuinely remount rather than re-render dirty state. */
  nonce: number;
}

const c = palettes.light;
const { spacing, radius, touch } = tokens;

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, nonce: 0 };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // logger.error always emits, dev or release, so this reaches logcat on a real
    // device — which is the only way to diagnose a field crash after the fact.
    logger.error(`[ErrorBoundary${this.props.label ? ` ${this.props.label}` : ''}]`, error, {
      componentStack: info.componentStack,
    });
  }

  retry = () => {
    this.setState((s) => ({ error: null, nonce: s.nonce + 1 }));
  };

  render() {
    const { error, nonce } = this.state;
    if (!error) {
      return (
        <View key={nonce} style={{ flex: 1 }}>
          {this.props.children}
        </View>
      );
    }

    return (
      <View
        style={{
          flex: 1,
          backgroundColor: c.surface,
          // No safe-area hook here: useSafeAreaInsets would throw outside the provider,
          // and the provider is one of the things this boundary exists to catch. The
          // status bar is ~24dp plus a cutout, so 64 clears it on every device we ship to.
          paddingTop: 64,
          paddingHorizontal: spacing.lg,
          paddingBottom: spacing.lg,
          gap: spacing.md,
        }}
      >
        <Text
          style={[type.monoEyebrow, { color: c.stateFailed, textTransform: 'uppercase' }]}
          testID="boundary-eyebrow"
        >
          {this.props.label ? `${this.props.label} · stopped` : 'stopped'}
        </Text>
        <Text style={[type.h1, { color: c.text }]}>This screen hit a problem</Text>
        <Text style={[type.bodyLg, { color: c.dim }]}>
          Nothing you submitted was lost. A job already sent keeps running on the server, and the
          verdict will be waiting under recent journeys.
        </Text>

        {/* The message is on screen, not only in logcat. An operator in a yard can read it
            back over the phone, which is the difference between a diagnosable report and
            "it just closed". */}
        <ScrollView
          style={{
            maxHeight: 200,
            backgroundColor: c.s2,
            borderRadius: radius.md,
            borderWidth: 1,
            borderColor: c.border,
          }}
          contentContainerStyle={{ padding: spacing.sm }}
        >
          <Text style={[type.monoValue, { color: c.dim2 }]} testID="boundary-message">
            {error.message || String(error)}
          </Text>
        </ScrollView>

        <View style={{ flex: 1 }} />

        <Pressable
          testID="boundary-retry"
          accessibilityRole="button"
          onPress={this.retry}
          style={{
            minHeight: touch.cta,
            borderRadius: radius.lg,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: c.primary,
          }}
        >
          <Text style={[type.label, { color: c.onPrimary }]}>Try again</Text>
        </Pressable>
      </View>
    );
  }
}
